import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { FiChevronDown, FiRadio, FiX } from "react-icons/fi";
import api from "../../../../services/api";
import { buildPropertyAlertRequestBody } from "../../../../utils/propertyAlertPayload";
import { savePendingPropertyAlert } from "../../../../utils/propertyAlertPending";
import { PROPERTY_TYPES } from "../TitleSearch/SearchFilter/constants";
import { getRadarPrefill, PropertyKindOption } from "./prefillFromSearch";
import {
  ModalOverlay,
  ModalPanel,
  ModalHeader,
  ModalHeaderLeft,
  ModalHeaderIcon,
  ModalTitle,
  ModalSubtitle,
  ModalClose,
  ModalBody,
  ModalFooter,
  FieldGroup,
  FieldLabel,
  FieldHint,
  TextInput,
  KindToggleRow,
  KindOption,
  RowTwoCols,
  NotifySection,
  NotifySectionTitle,
  CheckboxRow,
  CheckboxInput,
  CheckboxText,
  SelectWrapper,
  SelectNative,
  SelectChevron,
  SearchContextHint,
  FieldError,
  ErrorText,
  FooterButtonSecondary,
  FooterButtonPrimary,
} from "./styles";

type FieldErrors = Record<string, string>;

interface Props {
  open: boolean;
  onClose: () => void;
  searchParams: string;
  accessToken: string | undefined;
  user: { email: string; phone: string } | undefined;
}

function pickFirstApiErrors(
  raw: Record<string, string[] | string> | undefined
): FieldErrors {
  if (!raw) return {};
  const out: FieldErrors = {};
  Object.entries(raw).forEach(([key, val]) => {
    if (Array.isArray(val) && val[0]) out[key] = val[0];
    else if (typeof val === "string" && val) out[key] = val;
  });
  return out;
}

export default function PropertyRadarModal({
  open,
  onClose,
  searchParams,
  accessToken,
  user,
}: Props) {
  const history = useHistory();

  const [cityFromUrl, setCityFromUrl] = useState("");
  const [neighborhoodFromUrl, setNeighborhoodFromUrl] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [maxRent, setMaxRent] = useState("");
  const [minRooms, setMinRooms] = useState("");
  const [propertyTypeId, setPropertyTypeId] = useState("");
  const [propertyKind, setPropertyKind] =
    useState<PropertyKindOption>("residential");
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyWhatsapp, setNotifyWhatsapp] = useState(true);
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const clearFieldError = useCallback((key: string) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const resetFromSearch = useCallback(() => {
    const p = getRadarPrefill(searchParams);
    setCityFromUrl(p.cityFromUrl);
    setNeighborhoodFromUrl(p.neighborhoodFromUrl);
    setNeighborhood(p.neighborhoodInput);
    setMaxRent(p.maxRent);
    setMinRooms(p.minRooms);
    setPropertyTypeId(p.propertyTypeId);
    setPropertyKind(p.propertyKind);
    setNotifyEmail(true);
    setNotifyWhatsapp(true);
    setWhatsappOptIn(false);
    setFieldErrors({});
  }, [searchParams]);

  useEffect(() => {
    if (open) {
      resetFromSearch();
    }
  }, [open, resetFromSearch]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const validate = (): FieldErrors => {
    const e: FieldErrors = {};
    if (!neighborhood.trim() && !cityFromUrl.trim()) {
      e.neighborhood =
        "Informe o bairro desejado ou faça uma busca com cidade na listagem.";
    }
    const maxNum = Number(maxRent);
    if (!maxRent.trim() || Number.isNaN(maxNum) || maxNum < 0) {
      e.max_rent = "Informe um valor máximo de aluguel válido.";
    }
    const roomsNum = Number(minRooms);
    if (
      minRooms.trim() === "" ||
      Number.isNaN(roomsNum) ||
      roomsNum < 0 ||
      !Number.isInteger(roomsNum)
    ) {
      e.min_rooms = "Informe o número mínimo de quartos (inteiro).";
    }
    if (notifyWhatsapp && !whatsappOptIn) {
      e.whatsapp_opt_in =
        "Marque o opt-in do WhatsApp ou desative notificações por WhatsApp.";
    }
    return e;
  };

  const handleSubmit = async () => {
    setFieldErrors({});
    const localErrors = validate();
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      return;
    }

    const payload = buildPropertyAlertRequestBody({
      cityFromUrl,
      neighborhood,
      maxRent,
      minRooms,
      propertyTypeId,
      propertyKind,
      notifyEmail,
      notifyWhatsapp,
      whatsappOptIn,
    });

    if (!user || !accessToken) {
      savePendingPropertyAlert(payload);
      onClose();
      history.push({
        pathname: "/login",
        state: {
          from: "/favorites",
        },
      });
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/api/client/tenant/property-alerts", payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      onClose();
      history.push("/favorites?notice=propertyAlertSaved");
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { message?: string; errors?: Record<string, string[]> } };
      };
      const apiErrors = pickFirstApiErrors(ax.response?.data?.errors);
      if (Object.keys(apiErrors).length > 0) {
        setFieldErrors(apiErrors);
      } else {
        setFieldErrors({
          _general:
            ax.response?.data?.message ||
            "Não foi possível salvar o alerta. Tente novamente.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const renderSearchHint = () => {
    if (neighborhoodFromUrl) {
      return (
        <SearchContextHint>
          Bairro da busca atual: <strong>{neighborhoodFromUrl}</strong>. Ajuste
          acima se quiser outro — o valor do campo será enviado no alerta.
        </SearchContextHint>
      );
    }
    if (cityFromUrl) {
      return (
        <SearchContextHint>
          Indique o <strong>bairro</strong> desejado no campo acima. A cidade{" "}
          <strong>{cityFromUrl}</strong> da sua busca também será enviada no
          alerta.
        </SearchContextHint>
      );
    }
    return null;
  };

  const modal = (
    <ModalOverlay
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <ModalPanel
        role="dialog"
        aria-modal="true"
        aria-labelledby="property-radar-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalHeaderLeft>
            <ModalHeaderIcon aria-hidden>
              <FiRadio size={22} />
            </ModalHeaderIcon>
            <div>
              <ModalTitle id="property-radar-title">Radar de imóveis</ModalTitle>
              <ModalSubtitle>
                Defina o que você procura. Avisamos por e-mail e WhatsApp quando
                aparecer algo compatível.
              </ModalSubtitle>
            </div>
          </ModalHeaderLeft>
          <ModalClose type="button" onClick={onClose} aria-label="Fechar">
            <FiX size={22} />
          </ModalClose>
        </ModalHeader>

        <ModalBody>
          <FieldGroup>
            <FieldLabel htmlFor="radar-neighborhood">
              Bairro <FieldHint>(obrigatório se não houver cidade na busca)</FieldHint>
            </FieldLabel>
            <TextInput
              id="radar-neighborhood"
              value={neighborhood}
              onChange={(e) => {
                setNeighborhood(e.target.value);
                clearFieldError("neighborhood");
              }}
              placeholder="Ex.: Aldeota, Asa Sul..."
              autoComplete="address-level3"
              $invalid={!!fieldErrors.neighborhood}
              aria-invalid={!!fieldErrors.neighborhood}
              aria-describedby={
                fieldErrors.neighborhood ? "err-neighborhood" : undefined
              }
            />
            {fieldErrors.neighborhood && (
              <FieldError id="err-neighborhood" role="alert">
                {fieldErrors.neighborhood}
              </FieldError>
            )}
            {renderSearchHint()}
          </FieldGroup>

          <RowTwoCols>
            <FieldGroup>
              <FieldLabel htmlFor="radar-max">
                Valor máximo do aluguel (R$){" "}
                <FieldHint>(obrigatório)</FieldHint>
              </FieldLabel>
              <TextInput
                id="radar-max"
                type="number"
                inputMode="decimal"
                min={0}
                step={100}
                value={maxRent}
                onChange={(e) => {
                  setMaxRent(e.target.value);
                  clearFieldError("max_rent");
                }}
                placeholder="Ex.: 3500"
                $invalid={!!fieldErrors.max_rent}
                aria-invalid={!!fieldErrors.max_rent}
                aria-describedby={
                  fieldErrors.max_rent ? "err-max-rent" : undefined
                }
              />
              {fieldErrors.max_rent && (
                <FieldError id="err-max-rent" role="alert">
                  {fieldErrors.max_rent}
                </FieldError>
              )}
            </FieldGroup>
            <FieldGroup>
              <FieldLabel htmlFor="radar-rooms">
                Mín. de quartos <FieldHint>(obrigatório)</FieldHint>
              </FieldLabel>
              <TextInput
                id="radar-rooms"
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                value={minRooms}
                onChange={(e) => {
                  setMinRooms(e.target.value);
                  clearFieldError("min_rooms");
                }}
                placeholder="Ex.: 2"
                $invalid={!!fieldErrors.min_rooms}
                aria-invalid={!!fieldErrors.min_rooms}
                aria-describedby={
                  fieldErrors.min_rooms ? "err-min-rooms" : undefined
                }
              />
              {fieldErrors.min_rooms && (
                <FieldError id="err-min-rooms" role="alert">
                  {fieldErrors.min_rooms}
                </FieldError>
              )}
            </FieldGroup>
          </RowTwoCols>

          <FieldGroup>
            <FieldLabel htmlFor="radar-property-type">
              Tipo do imóvel <FieldHint>(opcional)</FieldHint>
            </FieldLabel>
            <SelectWrapper>
              <SelectNative
                id="radar-property-type"
                value={propertyTypeId}
                onChange={(e) => {
                  setPropertyTypeId(e.target.value);
                  clearFieldError("property_type_id");
                }}
                $invalid={!!fieldErrors.property_type_id}
                aria-invalid={!!fieldErrors.property_type_id}
                aria-describedby={
                  fieldErrors.property_type_id ? "err-property-type" : undefined
                }
              >
                <option value="">Não especificar</option>
                {PROPERTY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </SelectNative>
              <SelectChevron aria-hidden>
                <FiChevronDown size={20} strokeWidth={2.25} />
              </SelectChevron>
            </SelectWrapper>
            {fieldErrors.property_type_id && (
              <FieldError id="err-property-type" role="alert">
                {fieldErrors.property_type_id}
              </FieldError>
            )}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Segmento</FieldLabel>
            <KindToggleRow role="group" aria-label="Segmento do imóvel">
              <KindOption
                type="button"
                $active={propertyKind === "residential"}
                onClick={() => {
                  setPropertyKind("residential");
                  clearFieldError("segment");
                }}
              >
                Residencial
              </KindOption>
              <KindOption
                type="button"
                $active={propertyKind === "commercial"}
                onClick={() => {
                  setPropertyKind("commercial");
                  clearFieldError("segment");
                }}
              >
                Comercial
              </KindOption>
            </KindToggleRow>
            {fieldErrors.segment && (
              <FieldError role="alert">{fieldErrors.segment}</FieldError>
            )}
          </FieldGroup>

          <NotifySection>
            <NotifySectionTitle>Notificações</NotifySectionTitle>
            <CheckboxRow>
              <CheckboxInput
                type="checkbox"
                checked={notifyEmail}
                onChange={(e) => {
                  setNotifyEmail(e.target.checked);
                  clearFieldError("notify_email");
                }}
              />
              <CheckboxText>Avisar por e-mail</CheckboxText>
            </CheckboxRow>
            {fieldErrors.notify_email && (
              <FieldError role="alert">{fieldErrors.notify_email}</FieldError>
            )}
            <CheckboxRow>
              <CheckboxInput
                type="checkbox"
                checked={notifyWhatsapp}
                onChange={(e) => {
                  setNotifyWhatsapp(e.target.checked);
                  if (!e.target.checked) setWhatsappOptIn(false);
                  clearFieldError("notify_whatsapp");
                  clearFieldError("whatsapp_opt_in");
                }}
              />
              <CheckboxText>Avisar por WhatsApp</CheckboxText>
            </CheckboxRow>
            {fieldErrors.notify_whatsapp && (
              <FieldError role="alert">{fieldErrors.notify_whatsapp}</FieldError>
            )}
            {notifyWhatsapp && (
              <CheckboxRow>
                <CheckboxInput
                  type="checkbox"
                  checked={whatsappOptIn}
                  onChange={(e) => {
                    setWhatsappOptIn(e.target.checked);
                    clearFieldError("whatsapp_opt_in");
                  }}
                />
                <CheckboxText>
                  Autorizo receber mensagens no WhatsApp (opt-in obrigatório para
                  este canal)
                </CheckboxText>
              </CheckboxRow>
            )}
            {fieldErrors.whatsapp_opt_in && (
              <FieldError id="err-whatsapp-opt-in" role="alert">
                {fieldErrors.whatsapp_opt_in}
              </FieldError>
            )}
          </NotifySection>

          {fieldErrors._general && (
            <ErrorText role="alert">{fieldErrors._general}</ErrorText>
          )}
        </ModalBody>

        <ModalFooter>
          <FooterButtonSecondary type="button" onClick={onClose}>
            Cancelar
          </FooterButtonSecondary>
          <FooterButtonPrimary
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Salvando…" : "Salvar alerta"}
          </FooterButtonPrimary>
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );

  return createPortal(modal, document.body);
}
